import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  NO_VOCABULARY,
  parseBody,
  segmentCopy,
  smsCountFor,
  STOP_LINE,
  type Segment,
  type TokenVocabulary,
} from "./touchCopy";
import type { Touch } from "./emailSmsTypes";

export type PreviewFrame = "desktop" | "mobile";

type Props = {
  touch: Touch;
  values: Record<string, string>;
  /** The registry vocabulary, so the preview flags the same tokens the editor does. */
  vocabulary?: TokenVocabulary;
  fromName: string;
  fromAddress: string;
  smsSender: string;
  footerAddress: string;
  frame?: PreviewFrame;
  /** Dark renders the body on the near-black ground some clients default to. */
  theme?: "light" | "dark";
  showScaleLabel?: boolean;
};

const FRAME_WIDTH: Record<PreviewFrame, number> = { desktop: 600, mobile: 375 };

function Segments({ parts }: { parts: Segment[] }) {
  return (
    <>
      {parts.map((part, i) => (
        <span
          key={i}
          className={
            part.empty || part.unknown ? "touch-preview__token touch-preview__token--empty" : undefined
          }
          title={part.unknown ? `[${part.token}] is not a known merge field` : undefined}
        >
          {part.t}
        </span>
      ))}
    </>
  );
}

export function TouchPreview({
  touch,
  values,
  vocabulary = NO_VOCABULARY,
  fromName,
  fromAddress,
  smsSender,
  footerAddress,
  frame = "desktop",
  theme = "light",
  showScaleLabel = true,
}: Props) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const isSms = touch.ch === "sms";

  /*
      Width comes from the container, measured on mount and on window resize. Deliberately
      not a ResizeObserver on our own box: observing it fed a loop with the panel's
      scrollbar and made the SMS frame jitter.
  */
  useEffect(() => {
    if (isSms) return;
    const measure = () => {
      const el = outerRef.current;
      if (!el) return;
      setWidth((prev) => (Math.abs(el.clientWidth - prev) >= 2 ? el.clientWidth : prev));
    };
    measure();
    let timer: number;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [isSms]);

  // Content height only sets the scaled box's height; it never feeds back into the scale.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    setHeight((prev) => (Math.abs(el.offsetHeight - prev) >= 2 ? el.offsetHeight : prev));
  });

  if (isSms) {
    const text = touch.text ?? "";
    const count = smsCountFor(text, values, vocabulary);
    return (
      <div className="touch-preview" ref={outerRef}>
        <div className="touch-preview__phone-wrap">
          <div className="touch-preview__phone">
            <div className="touch-preview__notch" />
            <div className="touch-preview__sender">{smsSender}</div>
            <div className="touch-preview__bubble">
              <Segments parts={segmentCopy(text, values, vocabulary)} />
              <span className="touch-preview__stop">{STOP_LINE}</span>
            </div>
            <div className="touch-preview__stop-note">
              <i className="bi bi-lock" /> Opt-out line added by platform
            </div>
          </div>
          <div className="touch-preview__count">
            {count.n} of 160 characters with the opt-out line · {count.seg}{" "}
            {count.seg === 1 ? "segment" : "segments"} ({count.enc})
          </div>
        </div>
      </div>
    );
  }

  const frameWidth = FRAME_WIDTH[frame];
  const scale = width ? Math.min(1, width / frameWidth) : 1;
  const percent = Math.round(scale * 100);
  const blocks = parseBody(touch.body ?? "");

  return (
    <div className="touch-preview" ref={outerRef}>
      {showScaleLabel && percent < 100 && (
        <div className="touch-preview__scale">Scaled to fit · {percent}%</div>
      )}
      <div
        className="touch-preview__box"
        style={{
          width: Math.floor(frameWidth * scale),
          height: height ? Math.ceil(height * scale) : undefined,
        }}
      >
        <div
          ref={innerRef}
          className="touch-preview__inner"
          style={{
            width: frameWidth,
            transform: `scale(${scale})`,
            position: height ? "absolute" : "relative",
          }}
        >
          <div className="touch-preview__inbox">
            <div className="touch-preview__from">
              From: {fromName} &lt;{fromAddress}&gt;
            </div>
            <div className="touch-preview__subject">
              <Segments parts={segmentCopy(touch.subject ?? "", values, vocabulary)} />
            </div>
            <div className="touch-preview__pre">
              <Segments parts={segmentCopy(touch.pre ?? "", values, vocabulary)} />
            </div>
          </div>

          <div className={`touch-preview__card touch-preview__card--${theme}`}>
            {/* The brand the campaign resolves, not a constant -- this preview is shown to
                clients, and one school's name on another's approval page is the failure
                nobody forgets (MPN-2201). */}
            <div className="touch-preview__band">
              <span>
                {values["[BRAND NAME]"] || values["[SCHOOL NAME]"] || values.school_name
                  ? `${values["[BRAND NAME]"] || values["[SCHOOL NAME]"] || values.school_name} logo`
                  : "Logo · set on the brand"}
              </span>
            </div>
            <div className="touch-preview__hero-wrap">
              <div className="touch-preview__hero">Hero image · set on the catalog design</div>
            </div>
            <div className="touch-preview__body">
              {blocks.map((block, i) => {
                if (block.kind === "heading") {
                  return (
                    <div className="touch-preview__h" key={i}>
                      <Segments parts={segmentCopy(block.text, values, vocabulary)} />
                    </div>
                  );
                }
                if (block.kind === "button") {
                  return (
                    <div className="touch-preview__btn-wrap" key={i}>
                      <span className="touch-preview__btn">{block.text}</span>
                    </div>
                  );
                }
                return (
                  <p key={i}>
                    <Segments parts={segmentCopy(block.text, values, vocabulary)} />
                  </p>
                );
              })}
            </div>
            <div className="touch-preview__foot">
              You are receiving this because you contacted {values.school_short || "the college"}.{" "}
              Unsubscribe · {footerAddress}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TouchPreview;
